<template>
  <footer class="mt-16 md:mt-56" ref="footerEl">
    <!-- Navigation items -->
    <section class="navigation-items z-10 relative flex-col flex">
      <div class="foooter-container bg-white flex-1 h-full ml-7 md:ml-14 p-4 py-10 md:py-20 flex flex-col md:px-20 px-4">

        <!-- Navigation sections -->

        <ul class="flex navigation-section flex-wrap gap-12 lg:mt-16">
          <!-- Desktop version -->
          <li
            v-for="(section, idx) in navigationSection"
            :key="idx"
            class="nav-sec-item hidden lg:block"
          >
            <h5>{{ section.title }}</h5>
            <ul>
              <li
                v-for="(item, idxx) in section.items"
                :key="idxx"
              >
                <a v-if="item.externalPath" :href="item.externalPath" class="text-black" target="_blank">
                  {{ item.title }}
                </a>
                <nuxt-link v-else :to="item.path" class="text-black">
                  {{ item.title }}
                </nuxt-link>
              </li>
            </ul>
          </li>

          <!-- Mobile version -->
          <li class="flex lg:hidden fex-col w-full">
            <nv-collapse>
              <nv-collapse-item
                v-for="(section, idx) in navigationSection"
                :key="idx"
              >
                <div slot="header" class="nv-d-flex nv-align-items-center nv-justify-content-between">
                  <span class="nv-d-flex">
                    {{ section.title }}
                  </span>
                  <nv-icon>expand-more</nv-icon>
                </div>
                <ul slot="content" class="mobile-navigation-links nv-mb-2">
                  <li
                    v-for="(item, idxx) in section.items"
                    :key="idxx"
                    class="nv-mb-2"
                  >
                    <nuxt-link :to="item.path">
                      {{ item.title }}
                    </nuxt-link>
                  </li>
                </ul>
              </nv-collapse-item>
            </nv-collapse>
          </li>
        </ul>

      </div>
    </section>
    <section class="big-logo nnp-big-logo relative z-0 px-7 md:px-14 py-7 md:py-14">

      <!-- Copyright and Social networks -->
      <div class="flex justify-between items-center flex-col lg:flex-row py-7 copyright-area border-b border-slate-400">
        <div class="order-2 lg:order-1 my-6 w-full lg:w-auto lg:text-justify text-white lg:max-w-2xl">
          <strong class="copyright-message block">
            {{ $t('copyright', { currentYear: (new Date()).getFullYear() }) }}
          </strong>
          <span v-if="$config.public.REGISTRED_TRADEMARK" class="nv-my-1 nv-d-block">
            {{ $t('trademark_alert') }}
          </span>
        </div>


        <!-- Links -->
        <ul class="flex-row w-full lg:w-auto gap-4 flex lg:order-2 mt-8">
          <li
            v-for="(link, idx) in socialLinks"
            :key="idx"
          >
            <a
              :href="link.url"
              :title="link.title"
              target="_blank"
            >
              <img
                class="w-10 h-10 lg:w-12 lg:h-12"
                :src="link.image"
                :alt="link.alt"
              >
            </a>
          </li>
          </ul>
      </div>

      <!-- Big logo -->
      <img src="https://assets.nuinalp.com/logo/logo-typo-white.svg" alt="Nuinalp logo" class="w-full block mt-7 lg:mt-14 nnp-logo">
    </section>
  </footer>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap';
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const footerEl = useTemplateRef('footerEl');
const { t, locale } = useI18n();
const localePath = useLocalePath();
const { $currentCallbackUrl, $config } = useNuxtApp();


const utils = computed(() => routesBuild(locale.value, [
  { title: t('refound_policy'), url: '/legal/refound-policy', id: '' },
  { title: t('privacy_policy'), url: '/legal/privacy-policy', id: '' },
  { title: t('terms_of_use'), url: '/legal/terms-of-use', id: '' },
  {
    title: t('trademarks'), url: '/organizations', enabled: false, id: '',
  },
]));


const navigationSection = computed(() => navigationLinksBuild(locale.value, [
  {
    title: t('customer'),
    items: [
      { title: t('nvpn_product_title'), path: '/home/privacy/vpn', enabled: true },
      { title: t('DIGITAL_IDENTITY_PROTECTION'), path: '/home/security/identity-protection', enabled: false },
    ],
    enabled: true,
  },
  {
    title: t('business'),
    items: [
      { title: t('POSTAL_CODES'), path: '/business/address-location/postal-codes' },
    ],
    enabled: false,
  },
  {
    title: t('FREE_TOOLS_DOWNLOADS'),
    items: [
      { title: t('NNP_PASSWORD_GENERATOR'), path: '/home/tools/web/password_generator', enabled: true },
      { title: t('NNP_QR_SCANNER'), path: '/home/tools/mobile/qr_scanner', enabled: true },
    ],
    enabled: false,
  },
  {
    title: t('nuinalp_account'),
    items: [
      {
        title: t('sign_in'),
        externalPath: `https://accounts.nuinalp.com/sign-in?lang=${locale.value}&callback=${$currentCallbackUrl()}`,
      },
      {
        title: t('sign_up'),
        externalPath: `https://accounts.nuinalp.com/sign-up?lang=${locale.value}&callback=${$currentCallbackUrl()}`,
      },
      {
        title: t('about'),
        path: '/account/about',
      },
    ],
    enabled: false,
  },
  {
    title: t('developer'),
    items: [
      { title: t('nuiverse'), path: '/security' },
      { title: t('developer_center'), path: '/security' },
    ],
    enabled: false,
  },
  {
    title: t('legal'),
    items: [
      { title: t('terms_of_service'), path: '/legal/terms-of-service' },
      { title: t('privacy_policy'), path: '/legal/privacy-policy' },
      { title: t('refund_policy'), path: '/legal/refund-policy' },
      { title: t('SUBPROCESSORS'), path: '/legal/subprocessors' },
      {
        title: t('trademarks'), path: '/organizations', enabled: false,
      },
    ],
    enabled: true,
  },
  {
    title: t('company'),
    items: [
      { title: t('about'), path: '/about/company-info' },
      // { title: t('contact_us'), path: '/security' },
      // { title: t('news'), path: '/security', enabled: true },
      // { title: t('blog'), path: '/security', enabled: true },
      // { title: t('report_abuse'), path: '/security', enabled: true },
      { title: t('status_service'), enabled: true, externalPath: $config.public.NNP_STATUS_PANEL_URL as string },
    ],
  },
  {
    title: t('support'),
    items: [
      // { title: t('download_and_install'), path: '/security' },
      { title: t('helper_center'), externalPath: $config.public.NNP_SUPPORT_URL as string },
      // { title: t('comunity_forum'), path: '/security' },
      // { title: t('corporative_support'), path: '/security' },
      // { title: t('original_software'), path: '/security' },
    ],
    enabled: true,
  },
  {
    title: t('partners'),
    items: [
      { title: t('become_partner'), path: '/security' },
      { title: t('find_partner'), path: '/security' },
      { title: t('partner_policy'), path: '/security' },
    ],
    enabled: false,
  },
]));

const socialLinks = computed(() => socialLinksBuild(locale.value, [
  {
    title: t('facebook_link'), alias: 'fb', useItl: true, image: 'facebook-logo-meta.svg', alt: 'Facebook',
  },
  {
    title: t('instagram_link'), alias: 'ig', useItl: true, image: 'instagram-logo.svg', alt: 'Instagram',
  },
  {
    title: t('linkedin_link'), alias: 'liin', useItl: true, image: 'linkedin-logo.svg', alt: 'LinkedIn',
  },
]));

onMounted(() => {
  gsap.set('footer .nnp-big-logo', { yPercent: -50 })

  const uncover = gsap.timeline({ paused: true })

  uncover.to('footer .nnp-big-logo', { yPercent: 0, ease: 'sine.in' });

  ScrollTrigger.create({
    trigger: 'footer .navigation-items',
    start: 'bottom bottom',
    end: 'max',
    animation: uncover,
    scrub: true,
  });

  ScrollTrigger.refresh();
});


useResizeObserver(footerEl, (entries) => {
  ScrollTrigger.refresh();
})
</script>

<style lang="scss" scoped>
footer {
  background-color: var(--nv-theme-primary);
}

.navigation-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));

  a {
    color: #000 !important;
  }
}


.nnp-big-logo {
  a {
    color: #fff !important;
    
    &::after,
    &::before {
      background-color: #fff;
    }
  }
}

/*
-----------------------------------
* Navigation sections
--------------------------------------
*/
.nav-sec-item {
  ul {
    list-style: none;

    li {
      padding: 4px 0;
    }
  }
}

.nnp-logo {
  width: 100%;
  height: auto;
}

.foooter-container {
  min-height: 300px;
}

nv-collapse {
  width: 100%;
}

.foooter-container {
  transform: translateY(-80px);
  margin-bottom: -120px;
}


@media (screen(md)) {
  .nnp-big-logo {
    max-height: unset;
  }

  .foooter-container {
    transform: translateY(-120px);
    margin-bottom: -120px;
    min-height: 550px;
  }
}
</style>